import React from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  Table,
  Header,
  Button,
  Box,
  SpaceBetween,
  Pagination,
  TextFilter,
  StatusIndicator,
  Badge,
  ButtonDropdown,
  CollectionPreferences,
  Spinner
} from '@cloudscape-design/components';
import { 
  personalPromotionsStore,
  personalPromotionsFormModalStore 
} from '../stores/personalPromotions';
import { personalPromotionsStatsModalStore } from '../stores/personalPromotionsStatsModal';
import PersonalPromotionsStatsModal from './PersonalPromotionsStatsModal';
import { normalizeRiskModelValues } from '../helpers/riskModelOptions';

const getAccountTriggerConditionsForForm = (account = {}) => ({
  ...account,
  requiredSocialAccounts: Array.isArray(account.requiredSocialAccounts) ? account.requiredSocialAccounts : [],
  riskModel: normalizeRiskModelValues(account.riskModel)
});

const getBetTriggerConditionsForForm = (bet = {}) => {
  if (!Array.isArray(bet.all) || bet.all.length === 0) {
    return {};
  }

  return {
    all: bet.all.map(condition => ({
      minBetsCount: condition.minBetsCount,
      maxBetsCount: condition.maxBetsCount
    }))
  };
};

const getLegacyDepositThreshold = (data = {}) => ({
  minDepositAmount: data.minDepositAmount !== undefined ? data.minDepositAmount : 50000,
  maxDepositAmount: data.maxDepositAmount !== undefined ? data.maxDepositAmount : 10000000,
  bonusPercentAmount: data.bonusPercentAmount !== undefined ? data.bonusPercentAmount : 50
});

const normalizeDepositThresholds = (data = {}) => {
  const thresholds = Array.isArray(data.depositThresholds)
    ? data.depositThresholds
    : [getLegacyDepositThreshold(data)];

  return thresholds.map(threshold => ({
    minDepositAmount: Number(threshold.minDepositAmount) || 0,
    maxDepositAmount: Number(threshold.maxDepositAmount) || 0,
    bonusPercentAmount: Number(threshold.bonusPercentAmount) || 0
  }));
};

const getDataWithDepositThresholds = (data = {}) => {
  const depositThresholds = normalizeDepositThresholds(data);
  const depositData = { ...data };
  delete depositData.minDepositAmount;
  delete depositData.maxDepositAmount;
  delete depositData.bonusPercentAmount;

  return {
    ...depositData,
    depositThresholds: depositThresholds
  };
};

const formatRubles = (kopeks) => ((Number(kopeks) || 0) / 100).toLocaleString();

const getMaxBonusAmount = (threshold) => {
  const maxDepositAmount = Number(threshold.maxDepositAmount) || 0;
  const bonusPercentAmount = Number(threshold.bonusPercentAmount) || 0;
  return Math.floor(maxDepositAmount * bonusPercentAmount / 100);
};

function PersonalPromotions(props) {
  const store = useRecoilValue(personalPromotionsStore);
  const setStore = useSetRecoilState(personalPromotionsStore);
  const setFormModalStore = useSetRecoilState(personalPromotionsFormModalStore);
  const setStatsModalStore = useSetRecoilState(personalPromotionsStatsModalStore);

  const [sortingColumn, setSortingColumn] = React.useState(null);
  const [sortingDescending, setSortingDescending] = React.useState(false);
  
  const [filterText, setFilterText] = React.useState('');
  const [pageSize, setPageSize] = React.useState(
    parseInt(localStorage.getItem('personalPromotions.perPage')) || 10
  );
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [isPaginationLoading, setIsPaginationLoading] = React.useState(false);
  const [isFilterLoading, setIsFilterLoading] = React.useState(false);
  const [isSortLoading, setIsSortLoading] = React.useState(false);

  React.useEffect(() => {
    if (props.Interface) {
      props.Interface.loadPersonalPromotionsList = loadPersonalPromotions;
    }
    setStore(prev => ({
      ...prev,
      pagination: { ...prev.pagination, currentPage: 1, pageSize: pageSize }
    }));
    loadPersonalPromotions(1);
    setIsInitialized(true);
  }, []);

  React.useEffect(() => {
    if (!isInitialized) return;
    
    setStore(prev => ({
      ...prev,
      pagination: { ...prev.pagination, currentPage: 1, pageSize: pageSize }
    }));
    loadPersonalPromotions(1);
  }, [pageSize]);
  
  React.useEffect(() => {
    if (!isInitialized) return;
    
    const timeoutId = setTimeout(() => {
      setStore(prev => ({
        ...prev,
        pagination: { ...prev.pagination, currentPage: 1 }
      }));
      loadPersonalPromotions(1, 'filter');
    }, 500); // Debounce for 500ms
    
    return () => clearTimeout(timeoutId);
  }, [filterText]);

  React.useEffect(() => {
    if (!isInitialized) return;
    
    setStore(prev => ({
      ...prev,
      pagination: { ...prev.pagination, currentPage: 1 }
    }));
    loadPersonalPromotions(1, 'sort');
  }, [sortingColumn, sortingDescending]);

  const loadPersonalPromotions = async (page = 1, loadingSource = null) => {
    if (loadingSource === 'pagination') {
      setIsPaginationLoading(true);
    } else if (loadingSource === 'filter') {
      setIsFilterLoading(true);
    } else if (loadingSource === 'sort') {
      setIsSortLoading(true);
    } else {
      setStore(prev => ({ ...prev, loading: true, error: null }));
    }
    
    try {
      const requestParams = {
        page: page,
        limit: pageSize
      };
      
      if (filterText) {
        requestParams.search = filterText;
      }
      
      if (sortingColumn) {
        requestParams.sortBy = sortingColumn.sortingField;
        requestParams.sortOrder = sortingDescending ? 'DESC' : 'ASC';
      }
      
      const response = await props.Interface.request("get", {
        class: "data",
        module: "personalPromotions",
        method: "list"
      }, requestParams);

      if (response && response.data) {
        const items = response.data.map(item => ({
          ...item,
          priority: Number.isFinite(Number(item.priority)) ? Number(item.priority) : 0,
          data: getDataWithDepositThresholds(typeof item.data === 'string' ? JSON.parse(item.data) : item.data),
          triggerConditions: typeof item.triggerConditions === 'string' ? JSON.parse(item.triggerConditions) : item.triggerConditions,
          statistics: {
            shows: 0,
            activations: 0,
            totalBonus: 0,
            conversionRate: 0
          }
        }));

        const totalItems = response.total || items.length;
        const totalPages = Math.ceil(totalItems / pageSize);

        setStore(prev => ({
          ...prev,
          items: items,
          loading: false,
          pagination: {
            ...prev.pagination,
            totalItems: totalItems,
            totalPages: totalPages
          }
        }));
        setIsPaginationLoading(false);
        setIsFilterLoading(false);
        setIsSortLoading(false);
      } else {
        setStore(prev => ({
          ...prev,
          items: [],
          loading: false
        }));
        setIsPaginationLoading(false);
        setIsFilterLoading(false);
        setIsSortLoading(false);
      }
    } catch (error) {
      console.error('Error loading personal promotions:', error);
      setStore(prev => ({ 
        ...prev, 
        loading: false, 
        error: error.message || 'Failed to load promotions'
      }));
      setIsPaginationLoading(false);
      setIsFilterLoading(false);
      setIsSortLoading(false);
      props.Interface.addNoty("Error", "Failed to load promotions: " + (error.message || 'Unknown error'));
    }
  };

  const handleAddPromotion = () => {
    setFormModalStore(prev => {
      return { 
        ...prev, 
        isOpen: true,
        mode: 'add',
        editingId: null,
        formData: {
          internal_name: '',
          internal_description: '',
          type: 'money',
          status: 'active',
          priority: 0,
          image: '',
          imageFile: null,
          startDate: '',
          endDate: '',
          data: {
            depositThresholds: [
              {
                minDepositAmount: 50000,
                maxDepositAmount: 10000000,
                bonusPercentAmount: 50
              }
            ],
            bonusType: 'all',
            ttlMinutes: 10
          },
          triggerConditions: {
            frequency: [],
          balanceGuard: {
            minCurrentBetAmount: 10000,
            remainingBetsMultiplier: 5,
            checkAfterPlacingCurrentBet: true,
            comparison: '<='
          },
          dailyUtcWindows: [],
            deposit: {},
            account: {
              requiredSocialAccounts: []
            },
            bet: {}
          }
        }
      };
    });
  };

  const handleEditPromotion = (item) => {
    const accountTriggerConditions = (item.triggerConditions && item.triggerConditions.account) || {};

    const triggerConditions = {
      frequency: (item.triggerConditions && item.triggerConditions.frequency) || [],
      balanceGuard: {
        comparison: '<=',
        ...(item.triggerConditions && item.triggerConditions.balanceGuard),
      },
      dailyUtcWindows: (item.triggerConditions && item.triggerConditions.dailyUtcWindows) || [],
      deposit: (item.triggerConditions && item.triggerConditions.deposit) || {},
      account: getAccountTriggerConditionsForForm(accountTriggerConditions),
      bet: getBetTriggerConditionsForForm(item.triggerConditions && item.triggerConditions.bet)
    };

    setFormModalStore(prev => ({
      ...prev,
      isOpen: true,
      mode: 'edit',
      editingId: item.id,
      formData: {
        internal_name: item.internal_name || '',
        internal_description: item.internal_description || '',
        type: item.type,
        status: item.status,
        priority: Number.isFinite(Number(item.priority)) ? Number(item.priority) : 0,
        image: item.image,
        imageFile: null,
        startDate: item.startDate,
        endDate: item.endDate,
        data: getDataWithDepositThresholds(item.data),
        triggerConditions: triggerConditions
      }
    }));
  };

  const handleToggleStatus = async (item) => {
    const newStatus = item.status === 'active' ? 'inactive' : 'active';
    
    try {
      await props.Interface.request("set", {
        class: "data",
        module: "personalPromotions",
        method: "edit"
      }, {
        id: item.id,
        status: newStatus
      });
      
      setStore(prev => ({
        ...prev,
        items: prev.items.map(i => 
          i.id === item.id ? { ...i, status: newStatus } : i
        )
      }));
      
      props.Interface.addNoty("Success", `Promotion status changed to ${newStatus}`);
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };


  const handleDuplicatePromotion = async (item) => {
    const duplicatedTriggerConditions = JSON.parse(JSON.stringify(item.triggerConditions || {}));
    const duplicatedAccountTriggerConditions = duplicatedTriggerConditions.account || {};

    setFormModalStore(prev => ({
      ...prev,
      isOpen: true,
      mode: 'add',
      editingId: null,
      formData: {
        internal_name: item.internal_name || '',
        internal_description: item.internal_description || '',
        type: item.type,
        status: 'inactive',
        priority: Number.isFinite(Number(item.priority)) ? Number(item.priority) : 0,
        image: item.image || '',
        imageFile: null,
        startDate: item.startDate || '',
        endDate: item.endDate || '',
        data: getDataWithDepositThresholds(item.data),
        triggerConditions: {
          ...duplicatedTriggerConditions,
          dailyUtcWindows: (item.triggerConditions && item.triggerConditions.dailyUtcWindows) || [],
          balanceGuard: {
            comparison: '<=',
            ...(item.triggerConditions && item.triggerConditions.balanceGuard)
          },
          account: getAccountTriggerConditionsForForm(duplicatedAccountTriggerConditions),
          bet: getBetTriggerConditionsForForm(duplicatedTriggerConditions.bet)
        }
      }
    }));
  };

  const handleShowStats = (item) => {
    setStatsModalStore(prev => ({
      ...prev,
      show: true,
      data: item
    }));
  };

  const getPromotionTitle = (item) => {
    if (item.type === 'money') {
      const thresholds = normalizeDepositThresholds(item.data);
      const bonusLabel = thresholds.length > 1
        ? `${thresholds.length} thresholds`
        : `${thresholds[0]?.bonusPercentAmount || 0}%`;
      return `Money Bonus ${bonusLabel} - ${item.data?.bonusType || 'all'}`;
    } else if (item.type === 'freespins') {
      return `Freespins Promotion`;
    }
    return `Promotion #${item.id}`;
  };

  const getPromotionDescription = (item) => {
    const parts = [];
    
    const thresholds = normalizeDepositThresholds(item.data);
    if (thresholds.length > 0) {
      const thresholdParts = thresholds.map(threshold => (
        `from ${formatRubles(threshold.minDepositAmount)}₽ / bonus up to ${formatRubles(getMaxBonusAmount(threshold))}₽`
      ));
      parts.push(thresholdParts.join('; '));
    }
    
    if (item.data?.ttlMinutes) {
      parts.push(`Valid for: ${item.data.ttlMinutes} min`);
    }
    
    return parts.join(' | ') || 'No description';
  };

  const getPaymentTypes = (item) => {
    if (!item.data?.bonusType) return [];
    
    switch (item.data.bonusType) {
      case 'all':
        return ['fiat', 'crypto'];
      case 'fiat':
        return ['fiat'];
      case 'crypto':
        return ['crypto'];
      default:
        return [];
    }
  };


  const getStatusIndicator = (status) => {
    switch (status) {
      case 'active':
        return <StatusIndicator type="success">Active</StatusIndicator>;
      case 'inactive':
        return <StatusIndicator type="warning">Inactive</StatusIndicator>;
      case 'paused':
        return <StatusIndicator type="warning">Paused</StatusIndicator>;
      case 'ended':
        return <StatusIndicator type="error">Ended</StatusIndicator>;
      default:
        return <StatusIndicator type="info">{status || 'Undefined'}</StatusIndicator>;
    }
  };

  const getPaymentTypesBadges = (types) => {
    return types.map(type => (
      <Badge key={type} color={type === 'fiat' ? 'blue' : 'green'}>
        {type.charAt(0).toUpperCase() + type.slice(1)}
      </Badge>
    ));
  };

  const columnDefinitions = [
    {
      id: 'id',
      header: 'ID',
      cell: (item) => `#${item.id}`,
      sortingField: 'id',
      width: 80
    },
    {
      id: 'type',
      header: 'Type',
      cell: (item) => (
        <Badge color={item.type === 'money' ? 'blue' : 'green'}>
          {item.type === 'money' ? 'Money' : 'Freespins'}
        </Badge>
      ),
      sortingField: 'type',
      width: 100
    },
    {
      id: 'details',
      header: 'Details',
      cell: (item) => (
        <div>
          <div style={{ 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap',
            fontWeight: 'bold'
          }}>
            {getPromotionTitle(item)}
          </div>
          <div style={{ 
            color: '#666',
            fontSize: '12px',
            maxWidth: '300px',
            lineHeight: '1.2',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            whiteSpace: 'wrap',
            overflow: 'hidden'
          }}>
            {getPromotionDescription(item)}
          </div>
        </div>
      ),
      sortingField: 'id',
    },
    {
      id: 'priority',
      header: 'Priority',
      cell: (item) => (
        <div style={{ textAlign: 'center' }}>
          <strong>{Number.isFinite(Number(item.priority)) ? Number(item.priority) : 0}</strong>
        </div>
      ),
      sortingField: 'priority',
      width: 100
    },
    {
      id: 'status',
      header: 'Status',
      cell: (item) => (
        <div style={{ textAlign: 'center' }}>
          {getStatusIndicator(item.status)}
        </div>
      ),
      sortingField: 'status',
    },
    {
      id: 'bonusType',
      header: 'Bonus Type',
      cell: (item) => (
        <div style={{ textAlign: 'center', whiteSpace: 'nowrap', minWidth: '140px' }}>
          <SpaceBetween direction="horizontal" size="xs">
            {getPaymentTypesBadges(getPaymentTypes(item))}
          </SpaceBetween>
        </div>
      ),
      sortingField: 'bonusType',
      minWidth: 140
    },
    {
      id: 'bonus',
      header: 'Bonus',
      cell: (item) => (
        <div style={{ textAlign: 'center' }}>
          {item.type === 'money' ? (
            <SpaceBetween size="xxs">
              <div style={{ display: 'flex', justifyContent: 'center', gap: '10px'}} >
                {normalizeDepositThresholds(item.data).map((threshold, index) => (
                  <div key={index}>
                    <strong>{threshold.bonusPercentAmount}%</strong>
                    <br />
                    <small>from {formatRubles(threshold.minDepositAmount)}₽</small>
                  </div>
                ))}
              </div>
              <small>TTL: {item.data.ttlMinutes || 0} min</small>
            </SpaceBetween>
          ) : (
            <span>-</span>
          )}
        </div>
      ),
      sortingField: 'bonus',
      minWidth: 150
    },
    {
      id: 'dates',
      header: 'Dates',
      cell: (item) => (
        <div style={{ textAlign: 'center' }}>
          <div>From: {new Date(item.startDate).toLocaleDateString()}</div>
          <div>To: {new Date(item.endDate).toLocaleDateString()}</div>
        </div>
      ),
      sortingField: 'startDate'
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (item) => (
        <div style={{ 
          textAlign: 'center', 
          whiteSpace: 'nowrap', 
          minWidth: '150px',
          width: '150px',
          overflow: 'hidden'
        }}>
          <ButtonDropdown
            items={[
              { text: 'Edit', id: 'edit' },
              { text: item.status === 'active' ? 'Pause' : 'Activate', id: 'toggle' },
              { text: 'Statistics', id: 'stats' },
              { text: 'Duplicate', id: 'duplicate' }
            ]}
            onItemClick={(e) => {
              switch (e.detail.id) {
                case 'edit':
                  handleEditPromotion(item);
                  break;
                case 'toggle':
                  handleToggleStatus(item);
                  break;
                case 'stats':
                  handleShowStats(item);
                  break;
                case 'duplicate':
                  handleDuplicatePromotion(item);
                  break;
                default:
                  break;
              }
            }}
            expandToViewport
          >
            Actions
          </ButtonDropdown>
        </div>
      ),
      minWidth: 150
    }
  ];

  const filteredAndSortedItems = store.items;


  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        description="Manage personal promotions for users"
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={loadPersonalPromotions}>Refresh</Button>
            <Button variant="primary" onClick={handleAddPromotion}>
              Create Promotion
            </Button>
          </SpaceBetween>
        }
      >
        Personal Promotions
      </Header>

      <div style={{ 
        position: 'relative',
        opacity: (isPaginationLoading || isFilterLoading || isSortLoading) ? 0.5 : 1,
        transition: 'opacity 0.2s'
      }}>
        {(isPaginationLoading || isFilterLoading || isSortLoading) && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10
          }}>
            <Spinner size="large" />
          </div>
        )}
        <Table
          columnDefinitions={columnDefinitions}
          items={filteredAndSortedItems}
          loading={store.loading}
          sortingColumn={sortingColumn}
          sortingDescending={sortingDescending}
          onSortingChange={({ detail }) => {
            setSortingColumn(detail.sortingColumn);
            setSortingDescending(detail.isDescending);
          }}
          stickyHeader
          header={
            <Header
              counter={`(${store.pagination.totalItems})`}
              actions={null}
            >
              Promotions
            </Header>
          }
          filter={
            <TextFilter
              filteringText={filterText}
              onChange={({ detail }) => setFilterText(detail.filteringText)}
              filteringPlaceholder="Search by ID, type, status, bonus %..."
              filteringAriaLabel="Filter promotions"
            />
          }
          preferences={
            <CollectionPreferences
              title="Preferences"
              confirmLabel="Confirm"
              cancelLabel="Cancel"
              preferences={{ pageSize: pageSize }}
              pageSizePreference={{
                title: "Items per page",
                options: [
                  { value: 10, label: "10" },
                  { value: 20, label: "20" },
                  { value: 50, label: "50" },
                  { value: 100, label: "100" }
                ]
              }}
              onConfirm={(event) => {
                const newPageSize = event.detail.pageSize;
                setPageSize(newPageSize);
                localStorage.setItem('personalPromotions.perPage', newPageSize);
              }}
            />
          }
          pagination={
            <Pagination
              currentPageIndex={store.pagination.currentPage}
              onChange={({ detail }) => {
                const newPage = detail.currentPageIndex;
                setStore(prev => ({
                  ...prev,
                  pagination: { ...prev.pagination, currentPage: newPage }
                }));
                loadPersonalPromotions(newPage, 'pagination');
              }}
              pagesCount={store.pagination.totalPages || 1}
            />
          }
          empty={
            <Box textAlign="center" color="inherit">
              <b>No promotions</b>
              <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                Create your first personal promotion
              </Box>
              <Button onClick={handleAddPromotion}>Create Promotion</Button>
            </Box>
          }
        />
      </div>
      <PersonalPromotionsStatsModal key="stats-modal" Interface={props.Interface} />
    </SpaceBetween>
  );
}

export default PersonalPromotions;
